<template>
  <LoadingIndicator v-if="isLoadingChannels">Loading channels...</LoadingIndicator>
  <div v-else-if="showChannels && channels?.length > 0" class="channel-selector-container">
    <span class="dropdown">
      <select id="channel-filter" v-model="selectedChannelId">
        <option value="">All Channels</option>
        <option v-for="channel in sortedChannels" :key="channel.id" :value="channel.id">
          {{ channel.name }}
        </option>
      </select>
    </span>
    <button class="clear-filter" v-if="selectedChannelId" @click="clearSelectedChannel">Clear Filter</button>
  </div>
  <LoadingIndicator v-if="isLoadingVideoFiles">Loading video files...</LoadingIndicator>
  <div v-else class="table-container">
    <table>
      <thead>
        <tr>
          <SortableListHeader 
            column="stream_status" 
            :current-sort="sortColumn"
            default-direction="asc"
            @sort="handleSort"
          >
            Stream
          </SortableListHeader>
          <SortableListHeader 
            column="file" 
            :current-sort="sortColumn"
            default-direction="asc"
            @sort="handleSort"
          >
            File
          </SortableListHeader>
          <SortableListHeader 
            v-if="showChannels"
            column="channel" 
            :current-sort="sortColumn"
            default-direction="asc"
            @sort="handleSort"
          >
            Channel
          </SortableListHeader>
          <SortableListHeader 
            column="format" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Format
          </SortableListHeader>
          <SortableListHeader 
            column="resolution" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Resolution
          </SortableListHeader>
          <SortableListHeader 
            column="duration" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Duration
          </SortableListHeader>
          <SortableListHeader 
            column="fileSize" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            File Size
          </SortableListHeader>
          <SortableListHeader 
            column="uploaded" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Uploaded
          </SortableListHeader>
          <SortableListHeader 
            column="comments" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Notes
          </SortableListHeader>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="sortedVideoFiles.length === 0">
          <td :colspan="showChannels ? 10 : 9">No video files found.</td>
        </tr>
        <tr 
          v-for="videoFile in sortedVideoFiles" 
          :key="videoFile.id"
          @click="handleRowClick($event, videoFile)" 
          :class="{ clickable: isRowClickable() }"
          :title="isRowClickable() ? `View &quot;${videoFile.file_nickname || videoFile.file_name}&quot;` : null"
        >
          <td>
            <VideoFileStreamStatus :videoFile="videoFile" />
          </td>
          <td class="overflow">
            <div v-if="editingFile && editingFile.id === videoFile.id" class="inline-edit">
              <input 
                v-model="editNickname" 
                @blur="updateNickname(videoFile)" 
                @keyup.enter="updateNickname(videoFile)" 
                @keyup.esc="cancelEdit"
                ref="nicknameInput"
                :placeholder="videoFile.file_name"
              />
            </div>
            <div 
              v-else 
              @click.stop="isNicknameEditable ? startEdit(videoFile) : handleRowClick($event, videoFile)" 
              :class="{ 'editable-field': isNicknameEditable }"
            >
              <b v-if="videoFile.file_nickname" :title="`${videoFile.file_nickname} (${videoFile.file_name})`">{{ videoFile.file_nickname }}</b>
              <b v-else :title="videoFile.file_name">{{ videoFile.file_name }}</b>
            </div>
          </td>
          <td v-if="showChannels">
            <router-link 
              v-if="videoFile.channel_id" 
              :to="`/channels/${videoFile.channel_id}`" 
              :title="`View Channel &quot;${videoFile.channel_name}&quot;`"
              class="link-button"
              @click.stop
            >
              {{ videoFile.channel_name || 'Unknown Channel' }}
            </router-link>
            <span v-else class="no-channel">No Channel</span>
          </td>
          <td>
            <span :title="getFormatDisplay(videoFile.format)">
              {{ getFormatDisplayShort(videoFile.format) || '-' }}
            </span>
          </td>
          <td>{{ videoFile.file_resolution || '-' }}</td>
          <td>{{ formatDuration(videoFile.duration) || '-' }}</td>
          <td>{{ formatFileSize(videoFile.file_size) || '-' }}</td>
          <td :title="formatDatetime(videoFile.created_at)">{{ formatDate(videoFile.created_at) }}</td>
          <td>{{ videoFile.stream_status === 'Completed' ? videoFile.review_comments_count : '-' }}</td>
          <td>
            <span class="actions">
              <router-link 
              :to="`/videofiles/${videoFile.id}/encode`" 
              class="action-button edit"
              title="Encode"
              v-if="canEncodeVideo(videoFile)"
            >
              <font-awesome-icon icon="flag-checkered" />
            </router-link>
            <router-link 
              :to="`/admin/videofiles/${videoFile.id}/edit`" 
              class="action-button edit"
              title="Edit"
              v-if="isSuperuser()"
            >
              <font-awesome-icon icon="edit" />
            </router-link>
            <button 
              @click="confirmDelete(videoFile)" 
              class="action-button delete"
              title="Delete"
              v-if="isSuperuser()"
            >
              <font-awesome-icon icon="trash" />
            </button>
            <button 
              @click="showAlert('Video file deletion is coming soon.\nIn the meantime, please contact support:\nsupport@acuteimmersive.com')" 
              class="action-button"
              title="Delete"
              v-if="!isSuperuser() && canDeleteVideo(props.organizationId)"
            >
              <font-awesome-icon icon="trash" />
            </button>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useRouter } from 'vue-router';
import { isSuperuser, canViewVideo, canEditVideo, canDeleteVideo, canEncodeVideo } from '@/scripts/permissions';
import { FontAwesomeIcon } from '@/scripts/icons';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import { handleApiError, isInteractiveElement, formatDate, formatDatetime, formatDuration, formatFileSize, showConfirmDialog, showAlert, extractHeight } from '@/scripts/shared-utils';
import { getFormatDisplay, getFormatDisplayShort, getFormatSortOrder, getStreamStatusSortOrder } from '@/scripts/shared-video';
import SortableListHeader from './SortableListHeader.vue';
import VideoFileStreamStatus from './VideoFileStreamStatus.vue';
import { getAllChannels, getChannelVideoFiles, channelKeys } from '@/scripts/api/channelApi';
import { getAllVideoFiles, updateVideoFile, deleteVideoFile, videoFileKeys } from '@/scripts/api/videoFileApi';

const props = defineProps({
  channelId: {
    type: String,
    default: null
  },
  organizationId: {
    type: String,
    default: null
  },
  showChannels: {
    type: Boolean,
    default: false
  },
  filter: {
    type: Function,
    default: null
  }
});

const router = useRouter();
const queryClient = useQueryClient();
const selectedChannelId = ref('');
const editingFile = ref(null);
const editNickname = ref('');
const sortColumn = ref('uploaded');
const sortDirection = ref('desc');

const { data: channels, isLoading: isLoadingChannels } = useQuery({
  queryKey: channelKeys.getAllChannels(),
  queryFn: getAllChannels,
  enabled: props.showChannels,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching channels')
});

const currentChannelId = computed(() => {
  return selectedChannelId.value || props.channelId;
});

const videoFilesQueryKey = computed(() => {
  return currentChannelId.value ? channelKeys.getChannelVideoFiles(currentChannelId.value) : videoFileKeys.getAllVideoFiles();
});

const videoFilesQueryFunction = () => {
  if (currentChannelId.value) {
    return getChannelVideoFiles(currentChannelId.value);
  }
  if (isSuperuser()) {
    return getAllVideoFiles();
  }
  return Promise.resolve([]); // Return a resolved promise with an empty array
}

const { data: videoFiles, isLoading: isLoadingVideoFiles } = useQuery({
  queryKey: videoFilesQueryKey,
  queryFn: videoFilesQueryFunction,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching video files')
});

const filteredVideoFiles = computed(() => {
  let files = [...videoFiles.value];

  // Associate channel data with each video file
  if (props.showChannels && channels.value?.length > 0) {
    files = files.map(file => ({
      ...file,
      channel: channels.value.find(ch => ch.id === file.channel_id)
    }));
  }

  if (props.filter) {
    files = files.filter(props.filter);
  }
  
  return files;
});

const sortedVideoFiles = computed(() => {
  let sortedVideoFiles = [...filteredVideoFiles.value];
  sortedVideoFiles.sort((a, b) => {
    let comparison = 0;
    let lhs, rhs;

    switch (sortColumn.value) {
      case 'stream_status':
        lhs = getStreamStatusSortOrder(a.stream_status);
        rhs = getStreamStatusSortOrder(b.stream_status);
        comparison = lhs - rhs;
        break;

      case 'file':
        lhs = a.file_nickname || a.file_name || '';
        rhs = b.file_nickname || b.file_name || '';
        comparison = lhs.localeCompare(rhs);
        break;

      case 'channel':
        lhs = a.channel?.name || '';
        rhs = b.channel?.name || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'format':
        lhs = getFormatSortOrder(a.format);
        rhs = getFormatSortOrder(b.format);
        comparison = lhs - rhs;
        break;
        
      case 'resolution':
        lhs = extractHeight(a.file_resolution);
        rhs = extractHeight(b.file_resolution);
        comparison = lhs - rhs;
        break;
        
      case 'duration':
        lhs = a.duration || 0;
        rhs = b.duration || 0;
        comparison = lhs - rhs;
        break;
        
      case 'fileSize':
        lhs = a.file_size || 0;
        rhs = b.file_size || 0;
        comparison = lhs - rhs;
        break;
        
      case 'uploaded':
        lhs = new Date(a.created_at || 0).getTime();
        rhs = new Date(b.created_at || 0).getTime();
        comparison = lhs - rhs;
        break;

      case 'comments':
        lhs = a.stream_status === 'Completed' ? (a.review_comments_count || 0) : -1;
        rhs = b.stream_status === 'Completed' ? (b.review_comments_count || 0) : -1;
        comparison = lhs - rhs;
        break;

      default:
        break;
    }

    return sortDirection.value === 'asc' ? comparison : -comparison;
  });
  
  return sortedVideoFiles;
});

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const clearSelectedChannel = () => {
  selectedChannelId.value = '';
};

const handleSort = (sortData) => {
  sortColumn.value = sortData.column;
  sortDirection.value = sortData.direction;
};

const deleteMutation = useMutation({
  mutationFn: (videoFileId) => deleteVideoFile(videoFileId),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: videoFilesQueryKey.value });
    // Also invalidate the admin list if this is a superuser view
    if (isSuperuser() && !currentChannelId.value) {
      queryClient.invalidateQueries({ queryKey: videoFileKeys.getAllVideoFiles() });
    }
  },
  onError: (error) => {
    handleApiError(error, 'deleting video file');
  },
});

const updateNicknameMutation = useMutation({
  mutationFn: ({ videoFileId, nickname }) => updateVideoFile(videoFileId, { file_nickname: nickname }),
  onSuccess: (updatedFile) => {
    // Update the data in the cache for the current query
    queryClient.setQueryData(videoFilesQueryKey.value, (oldData) => {
      if (!oldData) return oldData;
      return oldData.map(file => 
        file.id === updatedFile.id ? updatedFile : file
      );
    });
    
    // Also update the detail query if it exists in the cache
    queryClient.setQueryData(videoFileKeys.getVideoFile(updatedFile.id), () => updatedFile);
    cancelEdit();
  },
  onError: (error) => {
    handleApiError(error, 'updating video file');
    cancelEdit();
  },
});

const confirmDelete = async (videoFile) => {
  if (await showConfirmDialog('Are you sure you want to delete this video file?')) {
    deleteMutation.mutate(videoFile.id);
  }
};

const isNicknameEditable = computed(() => {
  return !isSuperuser() && canEditVideo(props.organizationId);
});

const startEdit = (videoFile) => {
  editingFile.value = videoFile;
  editNickname.value = videoFile.file_nickname || videoFile.file_name;
  
  // Use nextTick to ensure the input is rendered before trying to focus it
  nextTick(() => {
    // Use the most reliable approach - direct DOM query for the active input
    const inputElement = document.querySelector('.inline-edit input');
    if (inputElement) {
      inputElement.focus();
    }
  });
};

const cancelEdit = () => {
  editingFile.value = null;
  editNickname.value = '';
};

const updateNickname = (videoFile) => {
  if (!editingFile.value) return;
  
  let nickname = editNickname.value.trim() || null;
  
  if (nickname === videoFile.file_name) {
    nickname = null;
  }
  
  if (nickname === (videoFile.file_nickname || null)) {
    cancelEdit();
    return;
  }
  
  updateNicknameMutation.mutate({ videoFileId: videoFile.id, nickname });
};

const isRowClickable = () => {
  return isSuperuser() || canViewVideo(props.organizationId);
}

const handleRowClick = (event, videoFile) => {
  if (!isInteractiveElement(event) && isRowClickable()) {
    router.push(`/videofiles/${videoFile.id}`);
  }
};
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/videos-list.css';
</style>
